import React, { FunctionComponent } from 'react';

import { rgba } from 'polished';
import { useTheme } from 'styled-components';

import { ResponsiveLine, Serie } from '@nivo/line';

import initialData from './initial-data.json';
import EarnedGraphTooltip from '@webapp/controllers/analytics/earned-graphic/earned-graph-tooltip';

interface AnalyticsWidgetGraphProps {
  className?: string;
  isExampleData: boolean;
  data: Serie[];
}

const formatMoney = (val: number): string => {
  const value = Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    //@ts-ignore
    notation: 'compact',
    compactDisplay: 'short',
  }).format(val);

  return value;
};

const ExampleData = [
  {
    id: 'streaming',
    color: '#086EE5',
    data: [
      { x: new Date('2023-01-01'), y: 5890 },
      { x: new Date('2023-02-01'), y: 2180 },
      { x: new Date('2023-03-01'), y: 3580 },
      { x: new Date('2023-04-01'), y: 1860 },
      { x: new Date('2023-05-01'), y: 5750 },
      { x: new Date('2023-06-01'), y: 1800 },
      { x: new Date('2023-07-01'), y: 5950 },
    ],
  },
  {
    id: 'merch',
    color: '#A1A5B4',
    data: [
      { x: new Date('2023-01-01'), y: 5090 },
      { x: new Date('2023-02-01'), y: 2780 },
      { x: new Date('2023-03-01'), y: 3880 },
      { x: new Date('2023-04-01'), y: 2860 },
      { x: new Date('2023-05-01'), y: 5050 },
      { x: new Date('2023-06-01'), y: 2800 },
      { x: new Date('2023-07-01'), y: 4850 },
    ],
  },
];

const AnalyticsWidgetGraph: FunctionComponent<AnalyticsWidgetGraphProps> = ({
  data,
  isExampleData,
}) => {
  const theme = useTheme();
  const [hasData, setHasData] = React.useState<Serie[]>(initialData);
  const [yLabels, setYLabels] = React.useState<number[]>([]);

  React.useEffect(() => {
    if (isExampleData) {
      setHasData(ExampleData);
      return;
    }
    if (data.length > 0) setHasData(data);
  }, [data, isExampleData]);

  React.useEffect(() => {
    const yPoints: number[] = [];
    data.map((d) => d.data.map((dd) => dd.y && yPoints.push(dd.y as number)));
    const yMax = Math.max(...yPoints);
    setYLabels([0, 0.3 * yMax, 0.6 * yMax, 0.9 * yMax]);
  }, [data]);

  const graphTheme = () => ({
    axis: {
      ticks: {
        line: {
          stroke: 'transparent',
        },
        text: {
          fill: rgba(theme.colors.inputLabel, 0.75),
          fontSize: 10,
        },
      },
    },
    grid: {
      line: {
        stroke: rgba(theme.colors.secondary, 0.75),
        strokeWidth: 1,
      },
    },
  });

  return (
    <ResponsiveLine
      data={hasData}
      margin={{ top: 0, right: 0, bottom: 5, left: 23 }}
      curve="linear"
      xScale={{ type: 'point' }}
      yScale={{
        type: 'linear',
        min: 'auto',
        max: 'auto',
      }}
      yFormat={(value) => formatMoney(Number(value))}
      axisTop={null}
      axisRight={null}
      axisLeft={{
        tickSize: 0,
        tickPadding: 4,
        tickRotation: 0,
        tickValues: yLabels,
        format: (value) => formatMoney(Number(value)).toUpperCase(),
      }}
      tooltip={({ point }) => (
        <EarnedGraphTooltip
          date={point.data.xFormatted}
          number={point.data.yFormatted}
        />
      )}
      axisBottom={null}
      pointLabelYOffset={0}
      theme={graphTheme()}
      lineWidth={2}
      colors={(d) => d.color}
      pointSize={0}
      pointBorderColor={{ from: 'serieColor' }}
      gridYValues={yLabels}
      enableGridX={false}
      enableGridY={true}
      legends={[]}
      isInteractive={false}
      animate={true}
      motionConfig="slow"
    />
  );
};

export default AnalyticsWidgetGraph;
